import * as React from 'react';
import { defaultRangeExtractor, useVirtualizer, type Range } from '@tanstack/react-virtual';
import { cn } from '@/lib/utils/cn';
import { TriCheckbox } from './tri-checkbox';
import {
  activeGroupIndex,
  buildGridItems,
  deriveTriState,
  gridKeyAction,
  inlineEditKeyAction,
  isTypingTarget,
  moveFocus,
  toggleAll,
  toggleSelection,
} from './grid-logic';

/**
 * WorkspaceDataGrid — virtualised, keyboard-driven grid (T-0140).
 *
 * Named `WorkspaceDataGrid` because `ui/data-grid.tsx` already exports a
 * small non-virtualised `DataGrid` (T-0101) that existing screens use.
 * This one is for workspace screens with up to several thousand rows.
 *
 * Reference: `.rq-tbl` in `docs/requirement-library/prototype/
 * selection.css` and `onKey` / `toggleCheck` in `selection.js`.
 *
 * The grid is controlled and owns no data. Selection and row focus come
 * in as props and go out through callbacks. The only state it keeps is
 * transient UI state: which cell is in inline edit, and the shift-click
 * anchor. The edit draft lives in the textarea itself, so typing never
 * re-renders rows.
 *
 * Performance notes for hosts:
 *   - Pass stable `columns`, `getRowId`, `groupBy`, `rowActions`
 *     (module constants or `useMemo` / `useCallback`). Rows are memoised
 *     and only the rows whose selected / focused / editing flags change
 *     re-render.
 *   - Rows must already be sorted by group when `groupBy` is set.
 *
 * Keyboard (suppressed while focus is in an input, textarea, select or
 * contenteditable, and when a modifier is held):
 *   Up / Down or k / j   move row focus
 *   Enter                open the focused row (`onOpen`)
 *   x or Space           toggle selection (Shift extends from the anchor)
 *   e                    start inline edit on the first editable column
 * Inline edit: Enter commits, Shift+Enter is a newline, Esc cancels,
 * blur commits.
 *
 * Accessibility: `role="grid"` with `aria-rowcount`, rows carry
 * `aria-rowindex` and `aria-selected`, and the focused row is exposed
 * through `aria-activedescendant` so the grid is a single tab stop even
 * though most rows are not in the DOM.
 */

const HEADER_HEIGHT = 36;
const GROUP_HEIGHT = 30;
const DEFAULT_ROW_HEIGHT = 40;

export interface WorkspaceGridColumn<T> {
  /** Stable key; also the React key and the id reported on edit. */
  key: string;
  header: React.ReactNode;
  /** CSS grid track size, for example `'76px'`. Default `minmax(0, 1fr)`. */
  width?: string;
  cell: (row: T) => React.ReactNode;
  /**
   * Makes the column inline-editable: return the current text for a row.
   * The grid renders a textarea seeded with this value.
   */
  editValue?: (row: T) => string;
  /** Accessible name for the edit textarea. Defaults to "Edit". */
  editLabel?: string;
  /**
   * Text column: single-line ellipsis by default, wraps in wrap mode.
   * Set false for chip / control columns that manage their own layout.
   */
  text?: boolean;
  headerClassName?: string;
  cellClassName?: string;
}

export interface WorkspaceDataGridProps<T> {
  rows: ReadonlyArray<T>;
  getRowId: (row: T) => string;
  columns: ReadonlyArray<WorkspaceGridColumn<T>>;
  /** Accessible name for the grid. */
  'aria-label': string;

  /** Group key per row; a sticky header row is inserted per group. */
  groupBy?: (row: T) => string;
  /** Custom group header content. Default: the key and a row count. */
  renderGroup?: (groupKey: string, count: number) => React.ReactNode;
  /**
   * Content for a footer row closing each group, for example an "add a
   * row to this group" control. Needs `groupBy`. Footers are not data
   * rows: they are skipped by row focus, selection and the row count.
   */
  renderGroupFooter?: (groupKey: string) => React.ReactNode;

  /** Show the leading checkbox column. */
  selectable?: boolean;
  selectedIds?: ReadonlySet<string>;
  onSelectionChange?: (next: Set<string>) => void;
  /** Locked rows show a locked checkbox and never change selection. */
  isRowLocked?: (row: T) => boolean;

  focusedId?: string | null;
  onFocusChange?: (id: string) => void;
  /** Enter on the focused row, or a click on a row. */
  onOpen?: (id: string) => void;
  /**
   * Default true. False makes a click only focus the row; Enter (or a
   * row action) still opens it. Use while rows are inline-editable and
   * opening would cover them: with a floating side panel, the first
   * click of a double-click would otherwise hide the cell being edited.
   */
  openOnClick?: boolean;

  /** Rows that may enter inline edit. Default: all rows. */
  isRowEditable?: (row: T) => boolean;
  /** Called only when the committed text differs from `editValue`. */
  onEditCommit?: (rowId: string, columnKey: string, value: string) => void;
  onEditCancel?: (rowId: string, columnKey: string) => void;

  /** Wrap text columns instead of single-line ellipsis. */
  wrap?: boolean;
  /** Trailing cell shown on row hover or focus (icon buttons). */
  rowActions?: (row: T) => React.ReactNode;
  /** CSS grid track size of the actions cell. Default `'44px'` (one icon). */
  rowActionsWidth?: string;
  rowClassName?: (row: T) => string | undefined;
  /** Estimated row height in px before measurement. Default 40. */
  estimateRowHeight?: number;
  overscan?: number;
  emptyState?: React.ReactNode;
  /**
   * A row after the last data row (T-0184), for example "Add area". It is
   * not a data row: row focus, selection and the virtualiser skip it.
   */
  footerRow?: React.ReactNode;
  /**
   * Listen for the grid keys on `document` instead of on the grid, so
   * j / k work without first clicking into the grid. Keys are still
   * ignored while typing, when a dialog is open, or when another
   * handler already handled the event. Use for a page with one grid.
   */
  globalKeys?: boolean;
  className?: string;
}

interface EditingCell {
  rowId: string;
  columnKey: string;
}

/** Handlers shared by every row; identity is stable for the grid's life. */
interface RowApi {
  /** A click on the row: focus it and, unless `openOnClick` is false, open it. */
  clickRow: (id: string) => void;
  toggleRow: (id: string, shift: boolean) => void;
  startEdit: (id: string, columnKey: string) => void;
  finishEdit: (id: string, columnKey: string, initial: string, value: string | null) => void;
}

function isInteractive(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== 'function') return false;
  return (
    (target as Element).closest(
      'button, a, input, textarea, select, [role="checkbox"], [role="radio"]',
    ) != null
  );
}

function modalDialogOpen(): boolean {
  return document.querySelector('[role="dialog"][data-state="open"]') != null;
}

export function WorkspaceDataGrid<T>({
  rows,
  getRowId,
  columns,
  'aria-label': ariaLabel,
  groupBy,
  renderGroup,
  renderGroupFooter,
  selectable = false,
  selectedIds,
  onSelectionChange,
  isRowLocked,
  focusedId = null,
  onFocusChange,
  onOpen,
  openOnClick = true,
  isRowEditable,
  onEditCommit,
  onEditCancel,
  wrap = false,
  rowActions,
  rowActionsWidth = '44px',
  rowClassName,
  estimateRowHeight = DEFAULT_ROW_HEIGHT,
  overscan = 10,
  emptyState,
  footerRow,
  globalKeys = false,
  className,
}: WorkspaceDataGridProps<T>) {
  const gridId = React.useId();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const anchorRef = React.useRef<string | null>(null);
  const [editing, setEditing] = React.useState<EditingCell | null>(null);

  const selected = selectedIds ?? EMPTY_SET;

  const hasGroupFooters = renderGroupFooter != null && groupBy != null;
  const { items, orderedIds, groupIndexes } = React.useMemo(
    () => buildGridItems(rows, getRowId, groupBy, hasGroupFooters),
    [rows, getRowId, groupBy, hasGroupFooters],
  );

  const { indexById, rowById, lockedIds, selectableIds } = React.useMemo(() => {
    const index = new Map<string, number>();
    const byId = new Map<string, T>();
    const locked = new Set<string>();
    const selectableList: string[] = [];
    items.forEach((item, i) => {
      if (item.kind !== 'row') return;
      index.set(item.id, i);
      byId.set(item.id, item.row);
      if (isRowLocked?.(item.row)) locked.add(item.id);
      else selectableList.push(item.id);
    });
    return { indexById: index, rowById: byId, lockedIds: locked, selectableIds: selectableList };
  }, [items, isRowLocked]);

  const template = React.useMemo(
    () =>
      [
        selectable ? '40px' : null,
        ...columns.map((c) => c.width ?? 'minmax(0, 1fr)'),
        rowActions ? rowActionsWidth : null,
      ]
        .filter(Boolean)
        .join(' '),
    [columns, selectable, rowActions, rowActionsWidth],
  );

  const virtualizerRef = React.useRef<ReturnType<
    typeof useVirtualizer<HTMLDivElement, Element>
  > | null>(null);

  // Keep the active group's header in the rendered range so it can be
  // pinned under the column header (TanStack "sticky" pattern).
  const rangeExtractor = React.useCallback(
    (range: Range) => {
      const base = defaultRangeExtractor(range);
      if (groupIndexes.length === 0) return base;
      const v = virtualizerRef.current;
      // The column header covers the first HEADER_HEIGHT px of the
      // viewport, so the "first visible" item is the one just below it.
      const top =
        v?.getVirtualItemForOffset((v.scrollOffset ?? 0) + HEADER_HEIGHT)?.index ??
        range.startIndex;
      const active = activeGroupIndex(groupIndexes, Math.max(top, range.startIndex));
      activeStickyRef.current = active;
      if (active < 0 || base.includes(active)) return base;
      return [active, ...base];
    },
    [groupIndexes],
  );
  const activeStickyRef = React.useRef(-1);

  const virtualizer = useVirtualizer<HTMLDivElement, Element>({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (items[i]?.kind === 'group' ? GROUP_HEIGHT : estimateRowHeight),
    getItemKey: (i) => {
      const item = items[i];
      if (!item) return i;
      if (item.kind === 'footer') return `f:${item.key}:${i}`;
      return item.kind === 'group' ? `g:${item.key}:${i}` : `r:${item.id}`;
    },
    overscan,
    scrollMargin: HEADER_HEIGHT,
    scrollPaddingStart: HEADER_HEIGHT + (groupBy ? GROUP_HEIGHT : 0),
    rangeExtractor,
  });
  virtualizerRef.current = virtualizer;

  // Row heights change wholesale when wrap flips; drop cached sizes.
  React.useEffect(() => {
    virtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrap]);

  // A row is taller while its inline editor is open; re-measure once the
  // editor closes so the rows below move back up straight away.
  React.useEffect(() => {
    if (editing == null) virtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // Keep the focused row in view when focus is moved from outside the
  // grid (a command palette jump, a "show me this row" link). `auto`
  // does nothing when the row is already visible, so clicks and the
  // grid's own key moves are unaffected.
  React.useEffect(() => {
    if (focusedId == null) return;
    const index = indexById.get(focusedId);
    if (index != null) virtualizerRef.current?.scrollToIndex(index, { align: 'auto' });
    // Only when the focused row changes, not when rows re-index.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedId]);

  // Latest props for the stable row API and the key handler.
  const latest = React.useRef({
    orderedIds,
    selected,
    lockedIds,
    focusedId,
    rowById,
    indexById,
    columns,
    isRowEditable,
    onSelectionChange,
    onFocusChange,
    onOpen,
    openOnClick,
    onEditCommit,
    onEditCancel,
  });
  latest.current = {
    orderedIds,
    selected,
    lockedIds,
    focusedId,
    rowById,
    indexById,
    columns,
    isRowEditable,
    onSelectionChange,
    onFocusChange,
    onOpen,
    openOnClick,
    onEditCommit,
    onEditCancel,
  };

  const api = React.useMemo<RowApi>(
    () => ({
      clickRow: (id) => {
        const l = latest.current;
        l.onFocusChange?.(id);
        if (l.openOnClick) l.onOpen?.(id);
      },
      toggleRow: (id, shift) => {
        const l = latest.current;
        if (!l.onSelectionChange) return;
        const next = toggleSelection({
          orderedIds: l.orderedIds,
          selected: l.selected,
          targetId: id,
          anchorId: anchorRef.current,
          shift,
          isLocked: (x) => l.lockedIds.has(x),
        });
        anchorRef.current = id;
        l.onSelectionChange(next);
      },
      startEdit: (id, columnKey) => {
        const l = latest.current;
        const row = l.rowById.get(id);
        if (!row) return;
        if (l.isRowEditable && !l.isRowEditable(row)) return;
        setEditing({ rowId: id, columnKey });
      },
      finishEdit: (id, columnKey, initial, value) => {
        const l = latest.current;
        setEditing(null);
        if (value != null && value !== initial) l.onEditCommit?.(id, columnKey, value);
        else l.onEditCancel?.(id, columnKey);
        // Hand focus back to the grid so the row keys keep working.
        scrollRef.current?.focus({ preventScroll: true });
      },
    }),
    [],
  );

  const handleKey = React.useCallback(
    (e: KeyboardEvent, global: boolean) => {
      if (e.defaultPrevented) return;
      if (global && (modalDialogOpen() || !scrollRef.current?.isConnected)) return;
      const typing = isTypingTarget(e.target);
      // Enter / Space on a button inside a row belongs to that button.
      if (!typing && (e.key === 'Enter' || e.key === ' ') && isInteractive(e.target)) return;
      const l = latest.current;
      const action = gridKeyAction(e, { typing, hasFocus: l.focusedId != null });
      if (!action) return;
      e.preventDefault();
      switch (action.type) {
        case 'move': {
          const next = moveFocus(l.orderedIds, l.focusedId, action.delta);
          if (next == null) return;
          l.onFocusChange?.(next);
          const index = l.indexById.get(next);
          if (index != null) virtualizerRef.current?.scrollToIndex(index, { align: 'auto' });
          return;
        }
        case 'open':
          if (l.focusedId != null) l.onOpen?.(l.focusedId);
          return;
        case 'toggle':
          if (l.focusedId != null) api.toggleRow(l.focusedId, action.shift);
          return;
        case 'edit': {
          const col = l.columns.find((c) => c.editValue);
          if (col && l.focusedId != null) api.startEdit(l.focusedId, col.key);
          return;
        }
      }
    },
    [api],
  );

  React.useEffect(() => {
    if (!globalKeys) return;
    const onKey = (e: KeyboardEvent) => handleKey(e, true);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [globalKeys, handleKey]);

  const headerState = deriveTriState(selectableIds, selected);
  const virtualItems = virtualizer.getVirtualItems();
  const activeSticky = activeStickyRef.current;
  const focusedRendered =
    focusedId != null && virtualItems.some((vi) => vi.index === indexById.get(focusedId));

  return (
    <div
      ref={scrollRef}
      role="grid"
      aria-label={ariaLabel}
      aria-rowcount={items.length + 1 + (footerRow ? 1 : 0)}
      aria-colcount={columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)}
      aria-multiselectable={selectable || undefined}
      aria-activedescendant={focusedRendered ? rowDomId(gridId, focusedId!) : undefined}
      tabIndex={0}
      onKeyDown={globalKeys ? undefined : (e) => handleKey(e.nativeEvent, false)}
      data-wrap={wrap || undefined}
      className={cn(
        'relative min-h-0 flex-1 overflow-auto bg-paper-0 text-body-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-amber-600',
        className,
      )}
    >
      <div
        role="row"
        aria-rowindex={1}
        className="sticky top-0 z-20 grid min-w-[560px] items-center border-b border-ink-100 bg-paper-100"
        style={{ gridTemplateColumns: template, height: HEADER_HEIGHT }}
      >
        {selectable ? (
          <div role="columnheader" className="flex items-center px-3">
            <TriCheckbox
              state={headerState}
              aria-label={headerState === 'on' ? 'Clear selection' : 'Select all rows'}
              onChange={() => onSelectionChange?.(toggleAll(selectableIds, selected))}
            />
          </div>
        ) : null}
        {columns.map((col) => (
          <div
            key={col.key}
            role="columnheader"
            className={cn('truncate px-3 text-meta font-medium text-ink-500', col.headerClassName)}
          >
            {col.header}
          </div>
        ))}
        {rowActions ? (
          <div role="columnheader">
            <span className="sr-only">Actions</span>
          </div>
        ) : null}
      </div>

      {items.length === 0 ? (
        emptyState
      ) : (
        <div
          role="rowgroup"
          className="relative min-w-[560px]"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualItems.map((vi) => {
            const item = items[vi.index];
            if (!item) return null;
            const offset = vi.start - virtualizer.options.scrollMargin;
            if (item.kind === 'group') {
              const sticky = vi.index === activeSticky;
              return (
                <div
                  key={vi.key}
                  role="row"
                  aria-rowindex={vi.index + 2}
                  data-group={item.key}
                  className="left-0 flex w-full items-center border-b border-ink-100 bg-paper-50 px-3 text-meta font-semibold text-ink-700"
                  style={
                    sticky
                      ? { position: 'sticky', top: HEADER_HEIGHT, zIndex: 10, height: GROUP_HEIGHT }
                      : {
                          position: 'absolute',
                          top: 0,
                          height: GROUP_HEIGHT,
                          transform: `translateY(${offset}px)`,
                        }
                  }
                >
                  <div role="gridcell" className="flex min-w-0 items-center">
                    {renderGroup ? (
                      renderGroup(item.key, item.count)
                    ) : (
                      <>
                        <span className="truncate">{item.key}</span>
                        <span className="ml-2 font-mono font-normal text-ink-500">
                          {item.count}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              );
            }
            if (item.kind === 'footer') {
              return (
                <div
                  key={vi.key}
                  ref={virtualizer.measureElement}
                  data-index={vi.index}
                  data-group-footer={item.key}
                  role="row"
                  aria-rowindex={vi.index + 2}
                  className="absolute left-0 top-0 flex min-h-row w-full items-center border-b border-ink-100 bg-paper-0 px-3 text-meta text-ink-500"
                  style={{ transform: `translateY(${offset}px)` }}
                >
                  <div role="gridcell" className="min-w-0 flex-1">
                    {renderGroupFooter?.(item.key)}
                  </div>
                </div>
              );
            }
            return (
              <GridRow
                key={vi.key}
                domId={rowDomId(gridId, item.id)}
                index={vi.index}
                offset={offset}
                measure={virtualizer.measureElement}
                row={item.row}
                id={item.id}
                columns={columns}
                template={template}
                selectable={selectable}
                selected={selected.has(item.id)}
                locked={lockedIds.has(item.id)}
                focused={item.id === focusedId}
                editingKey={editing?.rowId === item.id ? editing.columnKey : null}
                wrap={wrap}
                rowActions={rowActions}
                extraClassName={rowClassName?.(item.row)}
                api={api}
              />
            );
          })}
        </div>
      )}
      {footerRow ? (
        <div
          role="row"
          aria-rowindex={items.length + 2}
          className="flex min-h-row min-w-[560px] items-center border-b border-ink-100 bg-paper-0 px-3 text-meta text-ink-500"
        >
          <div role="gridcell" className="min-w-0 flex-1">
            {footerRow}
          </div>
        </div>
      ) : null}
    </div>
  );
}

WorkspaceDataGrid.displayName = 'WorkspaceDataGrid';

const EMPTY_SET: ReadonlySet<string> = new Set();

function rowDomId(gridId: string, id: string): string {
  return `${gridId}-row-${id}`;
}

interface GridRowProps<T> {
  domId: string;
  index: number;
  offset: number;
  measure: (el: Element | null) => void;
  row: T;
  id: string;
  columns: ReadonlyArray<WorkspaceGridColumn<T>>;
  template: string;
  selectable: boolean;
  selected: boolean;
  locked: boolean;
  focused: boolean;
  editingKey: string | null;
  wrap: boolean;
  rowActions?: (row: T) => React.ReactNode;
  extraClassName?: string;
  api: RowApi;
}

function GridRowInner<T>({
  domId,
  index,
  offset,
  measure,
  row,
  id,
  columns,
  template,
  selectable,
  selected,
  locked,
  focused,
  editingKey,
  wrap,
  rowActions,
  extraClassName,
  api,
}: GridRowProps<T>) {
  return (
    <div
      ref={measure}
      id={domId}
      data-index={index}
      data-row-id={id}
      data-focused={focused || undefined}
      data-selected={selected || undefined}
      role="row"
      aria-rowindex={index + 2}
      aria-selected={selectable ? selected : undefined}
      onClick={(e) => {
        if (isInteractive(e.target)) return;
        api.clickRow(id);
      }}
      className={cn(
        'group absolute left-0 top-0 grid min-h-row w-full cursor-pointer border-b border-ink-100 text-ink-700',
        wrap ? 'items-start' : 'items-center',
        focused
          ? 'bg-accent-amber-50 shadow-[inset_2px_0_0_var(--accent-amber-600)]'
          : selected
            ? 'bg-paper-100'
            : 'bg-paper-0 hover:bg-ink-100',
        extraClassName,
      )}
      style={{ gridTemplateColumns: template, transform: `translateY(${offset}px)` }}
    >
      {selectable ? (
        <div role="gridcell" className={cn('flex items-center px-3', wrap && 'pt-3')}>
          <TriCheckbox
            state={locked ? 'locked' : selected ? 'on' : 'off'}
            aria-label={locked ? 'Already included' : selected ? 'Deselect row' : 'Select row'}
            tabIndex={-1}
            onChange={(_next, e) => api.toggleRow(id, e.shiftKey)}
          />
        </div>
      ) : null}
      {columns.map((col) => {
        const isEditing = editingKey === col.key && col.editValue != null;
        const isText = col.text !== false;
        return (
          <div
            key={col.key}
            role="gridcell"
            onDoubleClick={col.editValue ? () => api.startEdit(id, col.key) : undefined}
            className={cn('min-w-0 px-3', wrap && !isEditing && 'py-2', col.cellClassName)}
          >
            {isEditing ? (
              <InlineEditor
                initial={col.editValue!(row)}
                label={col.editLabel ?? 'Edit'}
                onDone={(value) => api.finishEdit(id, col.key, col.editValue!(row), value)}
              />
            ) : isText ? (
              <div className={wrap ? 'whitespace-normal break-words' : 'truncate'}>
                {col.cell(row)}
              </div>
            ) : (
              col.cell(row)
            )}
          </div>
        );
      })}
      {rowActions ? (
        <div
          role="gridcell"
          className={cn(
            'invisible flex items-center justify-end px-1.5 group-hover:visible group-focus-within:visible group-data-[focused]:visible',
            wrap && 'pt-1.5',
          )}
        >
          {rowActions(row)}
        </div>
      ) : null}
    </div>
  );
}

const GridRow = React.memo(GridRowInner) as typeof GridRowInner;

interface InlineEditorProps {
  initial: string;
  label: string;
  /** `null` means cancelled; a string is the committed text. */
  onDone: (value: string | null) => void;
}

/**
 * Inline textarea. Uncontrolled on purpose: the draft never leaves the
 * DOM node until it is committed, so typing re-renders nothing.
 */
function InlineEditor({ initial, label, onDone }: InlineEditorProps) {
  const ref = React.useRef<HTMLTextAreaElement>(null);
  const done = React.useRef(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const finish = (value: string | null) => {
    if (done.current) return;
    done.current = true;
    onDone(value);
  };

  return (
    <textarea
      ref={ref}
      aria-label={label}
      defaultValue={initial}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // Nothing typed here may reach the grid or page shortcuts.
        e.stopPropagation();
        const action = inlineEditKeyAction(e);
        if (action === 'commit') {
          e.preventDefault();
          finish(e.currentTarget.value);
        } else if (action === 'cancel') {
          e.preventDefault();
          finish(null);
        }
      }}
      onBlur={(e) => finish(e.currentTarget.value)}
      className="my-1.5 block min-h-14 w-full resize-y rounded-chip border border-ink-300 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900 outline outline-2 outline-offset-1 outline-accent-amber-600"
    />
  );
}

/** Small icon button for the hover actions cell and panel headers. */
export const GridIconButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { 'aria-label': string }
>(({ className, type = 'button', ...props }, ref) => (
  <button
    ref={ref}
    type={type}
    className={cn(
      'inline-flex h-[26px] w-[26px] items-center justify-center rounded-chip text-ink-500 hover:bg-ink-100 hover:text-ink-900',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
      className,
    )}
    {...props}
  />
));
GridIconButton.displayName = 'GridIconButton';
